import i18n from '@/js/i18n.js';
import { formatMoney } from '@/js/utils/money.js';

const t = i18n.global.t;

/**
 * Product-kind & offer normalization shared by ProductCard and
 * ProductDetailPopup.
 *
 * A "product" in the marketplace can be:
 *  - PHYSICAL    shipped/picked-up goods (stock + delivery matter)
 *  - DIGITAL     downloadable/access-granted (no shipping)
 *  - SERVICE     booked/performed work (no stock, may be negotiable)
 *  - SUBSCRIPTION recurring access sold through multiple priced offers/plans
 *
 * Offers unify variants, price rules and explicit plan lists into one
 * selectable shape: { id, name, unitPrice, currency, billingPeriod, ... }.
 */

export const PRODUCT_KINDS = {
  PHYSICAL: 'PHYSICAL',
  DIGITAL: 'DIGITAL',
  SERVICE: 'SERVICE',
  SUBSCRIPTION: 'SUBSCRIPTION',
};

/** Category names (shopProductCreate list + campaign types) that map to kinds */
const CATEGORY_HINTS = [
  { match: /service|consultation|formation|repair|réparation|coiffure|plomberie/i, kind: PRODUCT_KINDS.SERVICE },
  { match: /subscription|abonnement|adhésion|membership/i, kind: PRODUCT_KINDS.SUBSCRIPTION },
  { match: /digital|numérique|ebook|logiciel|software|template|musique|vidéo/i, kind: PRODUCT_KINDS.DIGITAL },
];

const FULFILLMENT_HINTS = {
  DIGITAL: PRODUCT_KINDS.DIGITAL,
  EXTERNAL_CHECKOUT: PRODUCT_KINDS.DIGITAL,
  CONTACT_SELLER: null, // ambiguous — keep other signals
};

export const BILLING_PERIODS = {
  ONE_TIME: { key: 'marketplace.periodOneTime', shortKey: 'marketplace.periodOneTimeShort' },
  HOURLY: { key: 'marketplace.periodHourly', shortKey: 'marketplace.periodHourlyShort' },
  DAILY: { key: 'marketplace.periodDaily', shortKey: 'marketplace.periodDailyShort' },
  WEEKLY: { key: 'marketplace.periodWeekly', shortKey: 'marketplace.periodWeeklyShort' },
  MONTHLY: { key: 'marketplace.periodMonthly', shortKey: 'marketplace.periodMonthlyShort' },
  QUARTERLY: { key: 'marketplace.periodQuarterly', shortKey: 'marketplace.periodQuarterlyShort' },
  YEARLY: { key: 'marketplace.periodYearly', shortKey: 'marketplace.periodYearlyShort' },
};

/**
 * Resolve the display kind of a product.
 * Priority: explicit productType > fulfillmentType > category > default.
 */
export function productKind(product = {}) {
  const explicit = String(
    product.productType || product.kind || ''
  ).toUpperCase();
  if (PRODUCT_KINDS[explicit]) return explicit;

  const fulfillment = String(product.fulfillmentType || '').toUpperCase();
  if (FULFILLMENT_HINTS[fulfillment]) return FULFILLMENT_HINTS[fulfillment];

  // Subscription signal: any offer/rule carries a recurring period
  const rules = product.priceRules || [];
  if (
    rules.some((r) => r.billingPeriod && r.billingPeriod !== 'ONE_TIME') ||
    (product.offers || []).some((o) => o.billingPeriod && o.billingPeriod !== 'ONE_TIME')
  ) {
    return PRODUCT_KINDS.SUBSCRIPTION;
  }

  const category = String(product.category || '');
  for (const hint of CATEGORY_HINTS) {
    if (hint.match.test(category)) return hint.kind;
  }

  return PRODUCT_KINDS.PHYSICAL;
}

function normalizePeriod(raw) {
  const value = String(raw || 'ONE_TIME').toUpperCase();
  return BILLING_PERIODS[value] ? value : 'ONE_TIME';
}

/**
 * Build a unified offer list from every source available:
 *  1. explicit offers/plans (subscription tiers, service packages)
 *  2. variants (each becomes a purchasable option)
 *  3. price rules (BASE/MEMBER/GROUP_BUY tier prices)
 *  4. fallback: bare product price fields -> single ONE_TIME offer
 */
export function normalizeOffers(product = {}, variants = [], priceRules = []) {
  const currency = product.currency || 'XOF';
  const offers = [];

  // 1. Explicit offers/plans
  const explicit = product.offers || product.plans || [];
  (Array.isArray(explicit) ? explicit : []).forEach((o, i) => {
    offers.push({
      id: o.id || `offer-${i}`,
      name: o.name || o.title || '',
      unitPrice: Number(o.unitPrice ?? o.price ?? 0),
      currency: o.currency || currency,
      billingPeriod: normalizePeriod(o.billingPeriod),
      description: o.description || '',
      stockQuantity: o.stockQuantity,
      raw: o,
    });
  });

  // 2. Variants as purchasable options (skip when offers already exist)
  if (!offers.length) {
    (Array.isArray(variants) ? variants : []).forEach((v, i) => {
      const price = Number(v.currentUnitPrice ?? v.unitPrice ?? v.price ?? 0);
      if (!price && v.price !== 0) {
        // skip placeholder variants without pricing when others exist
        if ((variants || []).length > 1) return;
      }
      offers.push({
        id: v.id || `variant-${i}`,
        name: v.name || '',
        unitPrice: price,
        currency: v.currency || currency,
        billingPeriod: normalizePeriod(v.billingPeriod),
        description: v.sku ? `SKU ${v.sku}` : '',
        stockQuantity: v.stockQuantity,
        raw: v,
      });
    });
  }

  // 3. Price rules -> member/tier offers merged into a base offer
  const active = (Array.isArray(priceRules) ? priceRules : []).filter((r) => r.active !== false);
  const baseRule = active.find((r) => r.type === 'BASE') ||
    active.find((r) => r.type === 'GROUP_BUY') ||
    active.find((r) => typeof r.unitPrice === 'number');
  const memberRule = active.find((r) => r.type === 'MEMBER');

  if (baseRule && !offers.length) {
    offers.push({
      id: baseRule.id || 'offer-base',
      name: t('marketplace.standardOffer'),
      unitPrice: Number(baseRule.unitPrice || 0),
      currency: baseRule.currency || currency,
      billingPeriod: normalizePeriod(baseRule.billingPeriod),
      description: '',
      stockQuantity: product.stockQuantity,
      raw: baseRule,
    });
    if (memberRule && Number(memberRule.unitPrice) < Number(baseRule.unitPrice)) {
      offers.push({
        id: memberRule.id || 'offer-member',
        name: t('marketplace.memberOffer'),
        unitPrice: Number(memberRule.unitPrice || 0),
        currency: memberRule.currency || currency,
        billingPeriod: normalizePeriod(memberRule.billingPeriod),
        description: t('marketplace.membersOnly'),
        stockQuantity: product.stockQuantity,
        raw: memberRule,
      });
    }
  }

  // 4. Bare fallback
  if (!offers.length) {
    const bare = Number(
      product.currentPrice ?? product.basePrice ?? product.unitPrice ?? product.price ?? 0
    );
    offers.push({
      id: 'offer-default',
      name: t('marketplace.standardOffer'),
      unitPrice: bare,
      currency,
      billingPeriod: 'ONE_TIME',
      description: '',
      stockQuantity: product.stockQuantity,
      raw: null,
    });
  }

  return offers.filter((o) => Number.isFinite(o.unitPrice));
}

/**
 * Compact price summary for card display.
 */
export function summarizeOffers(offers = []) {
  if (!offers.length) return { min: 0, max: 0, count: 0, billingPeriod: 'ONE_TIME' };
  const prices = offers.map((o) => o.unitPrice);
  const periods = offers.map((o) => o.billingPeriod);
  const recurring = periods.find((p) => p !== 'ONE_TIME');
  return {
    min: Math.min(...prices),
    max: Math.max(...prices),
    count: offers.length,
    billingPeriod: recurring || 'ONE_TIME',
    hasRange: Math.min(...prices) !== Math.max(...prices),
  };
}

export function totalStock(offers = []) {
  const values = offers.map((o) => Number(o.stockQuantity)).filter((n) => !Number.isNaN(n));
  if (!values.length) return null; // unknown/unlimited
  if (values.some((n) => n === null || n === undefined)) return null;
  return values.reduce((a, b) => a + b, 0);
}

export function formatOfferPrice(amount, currency = 'XOF', billingPeriod = 'ONE_TIME') {
  // Marketplace prices are whole-unit amounts (XOF has no minor unit).
  const formatted = formatMoney(amount, currency, {
    decimals: 0,
    locale: i18n.global.locale || 'fr-FR',
  });
  const period = BILLING_PERIODS[billingPeriod];
  if (billingPeriod !== 'ONE_TIME' && period) {
    return `${formatted} ${t(period.shortKey)}`;
  }
  return formatted;
}
